import { faLinkSlash, faLink } from '@fortawesome/pro-regular-svg-icons';
import { faUser, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TIntegrationType } from '@gen2/api/integrations/api';
import { ActionModal } from '@gen2/app/components/action-modal/action-modal';
import { useActionModalStore } from '@gen2/app/components/action-modal/store';
import { useAuth } from '@gen2/hooks';
import { ButtonPropsVariantOverrides, Tooltip } from '@mui/material';
import { OverridableStringUnion } from '@mui/types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledButton,
  StyledCardButtonContainer,
  StyledContent,
  StyledIntCard,
  StyledIntCardContent,
  StyledIntCardDisabled,
  StyledIntCardHeader,
  StyledIntCardImg,
  StyledIntCardLevel,
  StyledIntCards,
  StyledIntegrations,
  StyledTitle,
} from './integrations.styled';
import { useIntegrationContext } from './useIntegrationContext';

interface IIntegration {
  provider: TIntegrationType;
  title: string;
  description?: string;
  img: string;
  link: string;
  connected: boolean;
  disabled: boolean;
  cardDataCy: string;
  buttonDataCy: string;
  buttonVariant: OverridableStringUnion<
    'text' | 'outlined' | 'contained',
    ButtonPropsVariantOverrides
  >;
  level: 'user' | 'account';
}

const Card = ({
  children,
  link,
  dataCy,
  disabled,
}: {
  children: React.ReactNode;
  link?: string;
  dataCy: string;
  disabled?: boolean;
}) => {
  if (link && !disabled) {
    return (
      <StyledIntCard
        data-cy={dataCy}
        to={link}
        style={{ textDecoration: 'none' }}
      >
        {children}
      </StyledIntCard>
    );
  }

  return (
    <StyledIntCardDisabled data-cy={dataCy}>{children}</StyledIntCardDisabled>
  );
};

type IntegrationCardsSectionProps = {
  title: string;
  items: IIntegration[];
  isLoading: boolean;
  onToggle?: (type: TIntegrationType) => void;
  imageStyle?: React.CSSProperties;
};

const IntegrationCardsSection: React.FC<IntegrationCardsSectionProps> = ({
  title,
  items,
  isLoading,
  onToggle,
  imageStyle,
}) => {
  const { t } = useTranslation('integrations');

  return (
    <StyledContent>
      <StyledTitle>{title}</StyledTitle>
      <StyledIntCards>
        {items.map((integration) => {
          const shouldShowDisconnect = Boolean(
            onToggle && integration.connected,
          );
          const buttonContent: React.ReactNode = shouldShowDisconnect ? (
            <>
              <FontAwesomeIcon icon={faLinkSlash} />
              <span>Disconnect</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faLink} />
              <span>Connect</span>
            </>
          );

          return (
            <Card
              key={integration.provider}
              dataCy={integration.cardDataCy}
              link={integration.link}
              disabled={integration.disabled}
            >
              <StyledIntCardHeader>
                <StyledIntCardImg
                  src={integration.img}
                  alt=""
                  style={imageStyle}
                />
              </StyledIntCardHeader>
              <StyledIntCardContent>
                <p>{integration.description ?? integration.title}</p>

                <StyledCardButtonContainer>
                  <StyledButton
                    variant={onToggle ? integration.buttonVariant : 'contained'}
                    color="secondary"
                    size="small"
                    onClick={
                      onToggle
                        ? () => onToggle(integration.provider)
                        : undefined
                    }
                    loading={isLoading}
                    disabled={isLoading || integration.disabled}
                    data-cy={integration.buttonDataCy}
                  >
                    {buttonContent}
                  </StyledButton>
                  <Tooltip title={t(`tooltip.level.${integration.level}`)}>
                    <StyledIntCardLevel>
                      <FontAwesomeIcon
                        icon={integration.level === 'user' ? faUser : faUsers}
                      />
                    </StyledIntCardLevel>
                  </Tooltip>
                </StyledCardButtonContainer>
              </StyledIntCardContent>
            </Card>
          );
        })}
      </StyledIntCards>
    </StyledContent>
  );
};

const Integrations = () => {
  const { disabled, connect, disconnect, isLoading, isConnected } =
    useIntegrationContext();
  const { user } = useAuth();
  const { t } = useTranslation('integrations');

  const { showModal } = useActionModalStore();

  const handleSwitchChange = async (type: TIntegrationType) => {
    if (!isConnected(type)) {
      connect(type);
    } else {
      const provider = t(`vendors.${type}`);

      const { isConfirmed } = await showModal({
        header: t('disconnect.modal.header', { provider }),
        message: 'disconnect.modal.message',
        messageParams: { provider },
        translationNamespace: 'integrations',
        closeButtonLabel: t('disconnect.modal.cancel_btn') ?? '',
        submitButtonLabel: t('disconnect.modal.confirm_btn') ?? '',
      });

      if (!isConfirmed) {
        return;
      }

      disconnect(type);
    }
  };

  const integrations = useMemo<IIntegration[]>(
    () => [
      {
        provider: 'google',
        title: t('google.thumbnail.title'),
        img: '/assets/int-google.png',
        link: '/integrations/google',
        connected: !!user?.integrations?.drive?.is_connected,
        disabled: disabled.google,
        cardDataCy: 'gdrive-link',
        buttonDataCy: 'connect-google-drive',
        buttonVariant: user?.integrations?.drive?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'user',
      },
      {
        provider: 'microsoft',
        title: t('microsoft.thumbnail.title'),
        img: '/assets/int-microsoft.png',
        link: '/integrations/microsoft',
        connected: !!user?.integrations?.onedrive?.is_connected,
        disabled: disabled.microsoft,
        cardDataCy: 'onedrive-link',
        buttonDataCy: 'connect-one-drive',
        buttonVariant: user?.integrations?.onedrive?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'user',
      },
      {
        provider: 'box',
        title: t('box.thumbnail.title'),
        img: '/assets/int-box.png',
        link: '/integrations/box',
        connected: !!user?.integrations?.box?.is_connected,
        disabled: disabled.box,
        cardDataCy: 'box-link',
        buttonDataCy: 'connect-box',
        buttonVariant: user?.integrations?.box?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'user',
      },
    ],
    [
      t,
      user?.integrations?.drive?.is_connected,
      user?.integrations?.onedrive?.is_connected,
      user?.integrations?.box?.is_connected,
      user?.organisations[0].accounts[0]?.integrations?.sharepoint
        ?.is_connected,
    ],
  );

  const accountsIntegrations = useMemo<IIntegration[]>(
    () => [
      {
        provider: 'sharepoint',
        title: t('microsoft.thumbnail.title'),
        img: '/assets/int-sharepoint.png',
        link: '/integrations/sharepoint',
        connected:
          !!user?.organisations[0].accounts[0]?.integrations?.sharepoint
            ?.is_connected,
        disabled: disabled.sharepoint,
        cardDataCy: 'sharepoint-link',
        buttonDataCy: 'connect-sharepoint',
        buttonVariant: user?.organisations[0].accounts[0]?.integrations
          ?.sharepoint?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'account',
      },
      {
        provider: 'google_account',
        title: t('google_account.thumbnail.title'),
        img: '/assets/int-google.png',
        link: '/integrations/google_account',
        connected:
          !!user?.organisations[0].accounts[0]?.integrations?.google_account
            ?.is_connected,
        disabled: disabled.google_account,
        cardDataCy: 'gdrive-link',
        buttonDataCy: 'connect-google-drive',
        buttonVariant: user?.organisations[0].accounts[0]?.integrations
          ?.google_account?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'account',
      },
      {
        provider: 'box_account',
        title: t('box_account.thumbnail.title'),
        img: '/assets/int-box_account.png',
        link: '/integrations/box_account',
        connected:
          !!user?.organisations[0].accounts[0]?.integrations?.box_account
            ?.is_connected,
        disabled: disabled.box_account,
        cardDataCy: 'box-link',
        buttonDataCy: 'connect-box',
        buttonVariant: user?.organisations[0].accounts[0]?.integrations
          ?.box_account?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'account',
      },
    ],
    [
      user?.organisations,
      t,
      user?.organisations[0].accounts[0]?.integrations?.sharepoint
        ?.is_connected,
      user?.organisations[0].accounts[0]?.integrations?.google_account
        ?.is_connected,
      user?.organisations[0].accounts[0]?.integrations?.box_account
        ?.is_connected,
    ],
  );

  const managedIntegrations = useMemo<IIntegration[]>(
    () => [
      {
        provider: 'hubspot',
        title: t('hubspot.thumbnail.title'),
        description: t('hubspot.thumbnail.desc') ?? '',
        img: '/assets/int-hubspot.png',
        link: '/integrations/managed/hubspot',
        connected:
          !!user?.organisations[0].accounts[0]?.integrations?.hubspot
            ?.is_connected,
        disabled: disabled.hubspot,
        cardDataCy: 'hubspot-link',
        buttonDataCy: 'connect-hubspot',
        buttonVariant: user?.organisations[0].accounts[0]?.integrations?.hubspot
          ?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'account',
      },
      {
        provider: 'salesforce',
        title: t('salesforce.thumbnail.title'),
        description: t('salesforce.thumbnail.desc') ?? '',
        img: '/assets/int-salesforce.png',
        link: '/integrations/managed/salesforce',
        connected:
          !!user?.organisations[0].accounts[0]?.integrations?.salesforce
            ?.is_connected,
        disabled: disabled.salesforce,
        cardDataCy: 'salesforce-link',
        buttonDataCy: 'connect-salesforce',
        buttonVariant: user?.organisations[0].accounts[0]?.integrations
          ?.salesforce?.is_connected
          ? 'outlined'
          : 'contained',
        level: 'account',
      },
    ],
    [
      t,
      user?.organisations[0].accounts[0]?.integrations?.hubspot?.is_connected,
      user?.organisations[0].accounts[0]?.integrations?.salesforce
        ?.is_connected,
    ],
  );

  // Check if any account-level integrations exist
  const hasAccountIntegrations = useMemo(
    () => accountsIntegrations.length > 0,
    [accountsIntegrations],
  );

  // Check if any managed integrations exist
  const hasManagedIntegrations = useMemo(
    () => managedIntegrations.length > 0,
    [managedIntegrations],
  );

  return (
    <StyledIntegrations>
      {hasAccountIntegrations && (
        <IntegrationCardsSection
          title="Account Cloud Storage"
          items={accountsIntegrations}
          isLoading={isLoading}
          onToggle={handleSwitchChange}
        />
      )}
      <IntegrationCardsSection
        title="User Cloud Storage"
        items={integrations}
        isLoading={isLoading}
        onToggle={handleSwitchChange}
      />
      {hasManagedIntegrations && (
        <IntegrationCardsSection
          title="Managed Integrations"
          items={managedIntegrations}
          isLoading={isLoading}
          imageStyle={{ objectFit: 'scale-down' }}
        />
      )}
      <ActionModal />
    </StyledIntegrations>
  );
};

export default Integrations;
